import {resolveDibHref} from "../../utils/dibUrls";
import {AssistantAnswer, SourceInfo, SourceSection} from "../../types";

/**
 * The reference engine's citation markers (format 2). A marker is
 * START family DELIMITER id [DELIMITER id ...] STOP with the three private-use
 * characters below. The backend resolves value, visual and company markers
 * before it streams, so an answer only ever carries cite markers here.
 *
 * This module turns those markers into the [n] form the rest of the renderer
 * already understands, with one display number per cited section, numbered in
 * order of first appearance.
 */
export const ENGINE_START = "";
export const ENGINE_STOP = "";
export const ENGINE_DELIMITER = "";
export const ENGINE_FORMAT = 2;

const MARKER = new RegExp(
    `${ENGINE_START}([a-z]+)((?:${ENGINE_DELIMITER}[^${ENGINE_START}${ENGINE_STOP}${ENGINE_DELIMITER}\\n]+)+)${ENGINE_STOP}`,
    "g"
);
const PRIVATE_USE = /[-]/g;
const LOCATOR = /^L\d+(?:-L\d+)?$/;

/**
 * True when the answer was written by the reference engine. The format field
 * arrives with the envelope suffix, so while the answer streams the decoded
 * content is checked for a marker instead.
 */
export const isEngineFormat = (
    answer: AssistantAnswer | null | undefined,
    content: string | undefined
): boolean =>
    answer?.format === ENGINE_FORMAT ||
    (typeof content === "string" && content.includes(ENGINE_START));

export type EngineMarker = {family: string; ids: string[]; index: number; length: number};

/** Every complete marker in the text, in order. */
export const parseEngineMarkers = (text: string): EngineMarker[] => {
    const markers: EngineMarker[] = [];
    for (const match of text.matchAll(MARKER)) {
        const fields = match[2].split(ENGINE_DELIMITER).filter((f) => f.length > 0);
        const ids =
            match[1] === "cite" && fields.length > 1 && LOCATOR.test(fields[fields.length - 1])
                ? fields.slice(0, -1)
                : fields;
        markers.push({family: match[1], ids, index: match.index ?? 0, length: match[0].length});
    }
    return markers;
};

/** Removes every marker and any stray marker character. For plain-text surfaces. */
const MARKER_WITH_SPACE = new RegExp(` ?${MARKER.source}`, "g");

const hasMarkerCharacter = (text: string): boolean =>
    text.includes(ENGINE_START) || text.includes(ENGINE_STOP) || text.includes(ENGINE_DELIMITER);

/** Text without marker characters is returned as it is, so legacy answers stay unchanged. */
export const stripEngineMarkers = (text: string): string => {
    if (!hasMarkerCharacter(text)) return text;
    return cutUnclosedEngineMarker(text)
        .replace(MARKER_WITH_SPACE, "")
        .replace(PRIVATE_USE, "")
        .replace(/ +([.,;:!?])/g, "$1")
        .trimEnd();
};

/**
 * While the answer streams, the text from an unmatched start character is held
 * back until its stop character arrives. In a complete answer such a tail can
 * never close, so it is dropped.
 */
export const cutUnclosedEngineMarker = (text: string): string => {
    const start = text.lastIndexOf(ENGINE_START);
    if (start === -1) return text;
    const stop = text.indexOf(ENGINE_STOP, start);
    return stop === -1 ? text.slice(0, start) : text;
};

/**
 * TEMPORARY, see dibkunnskap/ai-backend#364. Until the researcher output has a
 * grounding check, the answer shows the old behaviour: one chip per source,
 * linking to the source's best section, however many sections the answer cites.
 * The backend streams and stores every cited section, so removing this switch
 * (and the `collapse` branch in buildEngineCitations) restores per-section chips
 * for stored answers too.
 */
export const COLLAPSE_CITATIONS_TO_SOURCE = true;

export type EngineCitations = {
    /** The answer text with each cite marker replaced by [n] display markers. */
    text: string;
    /** One source entry per cited section, index = display number (0-based). */
    items: SourceInfo[];
    /** The block ids cited, in display order. */
    citedIds: string[];
};

const findSection = (
    sources: SourceInfo[] | undefined,
    id: string
): {source: SourceInfo; section: SourceSection} | null => {
    for (const source of sources ?? []) {
        const section = source.sections?.find((s) => s.id === id);
        if (section) return {source, section};
    }
    return null;
};

/**
 * Builds the display numbering for an engine-format answer. A cite marker with
 * several ids becomes one [n] per id. An id with no matching section in the
 * message's sources renders nothing.
 */
export const buildEngineCitations = (
    content: string,
    sources: SourceInfo[] | undefined,
    collapse: boolean = COLLAPSE_CITATIONS_TO_SOURCE
): EngineCitations => {
    const order = new Map<string, number>();
    const items: SourceInfo[] = [];
    const citedIds: string[] = [];
    content = cutUnclosedEngineMarker(content);
    // Models occasionally put a citation where Markdown expects a URL. Resolve
    // only a single known section, keeping the marker for normal attribution.
    // Otherwise retain the label and citations without inventing a destination.
    content = content.replace(
        new RegExp(`(!?)\\[([^\\]\\n]+)\\]\\(\\s*(${MARKER.source})\\s*\\)`, "g"),
        (_match, image: string, label: string, marker: string) => {
            const parsed = parseEngineMarkers(marker)[0];
            const ids = [...new Set(parsed?.family === "cite" ? parsed.ids : [])];
            const found = ids.length === 1 ? findSection(sources, ids[0]) : null;
            const url = found ? sectionUrl(resolveDibHref(found.source.url), found.section.sectionId) : "";
            // Angle destinations protect parentheses/spaces in source URLs.
            const destination = url.replace(/[<>\r\n]/g, (c) => encodeURIComponent(c));
            return !image && destination
                ? `[${label}](<${destination}>) ${marker}`
                : `${label} ${marker}`;
        }
    );

    const numberFor = (id: string): number | null => {
        const found = findSection(sources, id);
        if (!found) return null;
        if (!citedIds.includes(id)) citedIds.push(id);
        const {source, section} = found;
        // One number per source: the source keeps its own url, whose fragment is
        // the best-scoring section of the first search that returned it.
        const key = collapse ? `source:${source.index}` : id;
        const known = order.get(key);
        if (known !== undefined) return known;
        const index = items.length;
        order.set(key, index);
        if (collapse) {
            // A PDF opens at its first cited section, not at the source's best hit.
            items.push({...source, index, ...sectionPosition(section)});
            return index;
        }
        items.push({
            ...source,
            index,
            id,
            segmentName: section.title || source.segmentName,
            url: sectionUrl(source.url, section.sectionId),
            sections: [section],
            // A PDF section opens at its own page, not at the source's best hit.
            ...sectionPosition(section),
        });
        return index;
    };

    const text = content.replace(MARKER, (match, family: string, body: string) => {
        if (family !== "cite") return "";
        const fields = body.split(ENGINE_DELIMITER).filter((f: string) => f.length > 0);
        const ids = fields.length > 1 && LOCATOR.test(fields[fields.length - 1]) ? fields.slice(0, -1) : fields;
        const numbers = ids
            .map(numberFor)
            .filter((n: number | null): n is number => n !== null);
        return [...new Set(numbers)].map((n: number) => `[${n}]`).join("");
    });

    return {text: text.replace(PRIVATE_USE, ""), items, citedIds};
};

/** A PDF section's position, to override the source's; empty when the section has none. */
const sectionPosition = (section: SourceSection): Pick<SourceInfo, "pageIndex" | "y"> =>
    section.pageIndex != null ? {pageIndex: section.pageIndex, y: section.y ?? null} : {};

/** The source url with the section as its fragment, so the preview opens at that section. */
const sectionUrl = (url: string, sectionId: string): string => {
    if (!url || !sectionId) return url;
    const base = url.split("#")[0];
    return `${base}#${sectionId}`;
};

/** True when a source has at least one section among the cited ids. */
export const sourceIsCited = (source: SourceInfo, citedIds: string[]): boolean =>
    !!source.sections?.some((s) => citedIds.includes(s.id));

/** Accepts the SSE spelling (sectionid, sectiontitle, chunkids, pageindex) and the stored spelling. */
export const normalizeSections = (raw: unknown): SourceSection[] | undefined => {
    if (!Array.isArray(raw)) return undefined;
    const sections: SourceSection[] = [];
    for (const entry of raw) {
        if (!entry || typeof entry !== "object") continue;
        const e = entry as Record<string, unknown>;
        const id = typeof e.id === "string" ? e.id : "";
        if (!id) continue;
        const pageIndex = e.pageIndex ?? e.pageindex;
        sections.push({
            id,
            sectionId: String(e.sectionId ?? e.sectionid ?? ""),
            title: typeof (e.title ?? e.sectiontitle) === "string" ? ((e.title ?? e.sectiontitle) as string) : undefined,
            chunkIds: Array.isArray(e.chunkIds ?? e.chunkids) ? ((e.chunkIds ?? e.chunkids) as number[]) : [],
            pageIndex: typeof pageIndex === "number" ? pageIndex : undefined,
            y: typeof e.y === "number" ? e.y : undefined,
        });
    }
    return sections;
};
